import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { MessageCircle, X, ArrowLeft, Send, Search } from 'lucide-react';
import { chatAPI } from '../../lib/chatAPI';
import { useChatSocket } from '../../hooks/useChatSocket';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

/** Beep curto sintetizado via Web Audio API (sem arquivo mp3). */
function playBeep() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
    setTimeout(() => { try { ctx.close(); } catch (e) { void e; } }, 500);
  } catch (e) {
    // ignore
  }
}

const STATUS_COLORS = {
  online: 'bg-emerald-500',
  ausente: 'bg-amber-500',
  ocupado: 'bg-rose-500',
  offline: 'bg-slate-400',
};
const STATUS_LABEL = {
  online: 'Online',
  ausente: 'Ausente',
  ocupado: 'Ocupado',
  offline: 'Offline',
};

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay
      ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch (e) { return ''; }
};

export default function ChatWidget() {
  const { user, token } = useAuth();
  const [chatEnabled, setChatEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('list'); // list | conversation
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  const [typingUsers, setTypingUsers] = useState({}); // user_id -> timeout id
  const [manualStatus, setManualStatus] = useState(null);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const messagesEndRef = useRef(null);
  const typingSentAt = useRef(0);

  // Busca config inicial
  useEffect(() => {
    if (!user || !token) return;
    chatAPI.getConfig().then(r => setChatEnabled(!!r.data?.enabled)).catch(() => setChatEnabled(false));
  }, [user, token]);

  // Handler de eventos WS
  const handleWsEvent = useCallback((ev) => {
    if (!ev) return;
    if (ev.type === 'message') {
      const m = ev.message;
      const other = m.from_user_id === user.id ? m.to_user_id : m.from_user_id;

      setMessages(prev => {
        // se conversa aberta com esse usuário, adiciona
        if (selected && selected.id === other) {
          if (prev.some(x => x.id === m.id)) return prev;
          return [...prev, m];
        }
        return prev;
      });

      // Atualiza lista de contatos: preview + unread + reordenação
      setUsers(prev => {
        const idx = prev.findIndex(u => u.id === other);
        if (idx === -1) return prev;
        const arr = [...prev];
        const wasMine = m.from_user_id === user.id;
        const isViewingThis = selected && selected.id === other && document.visibilityState === 'visible';
        arr[idx] = {
          ...arr[idx],
          last_message: m,
          unread: (!wasMine && !isViewingThis) ? (arr[idx].unread || 0) + 1 : arr[idx].unread || 0,
        };
        // move para o topo
        const [pop] = arr.splice(idx, 1);
        arr.unshift(pop);
        return arr;
      });

      // Notificação sonora e badge global se mensagem é PARA mim
      if (m.from_user_id !== user.id) {
        playBeep();
        setTotalUnread(t => t + 1);
        // Se conversa aberta com esse usuário, marca como lida
        if (selected && selected.id === other && open) {
          chatAPI.markRead(other).catch(() => {});
          // decrementa o badge global também
          setTotalUnread(t => Math.max(0, t - 1));
        }
      }
    } else if (ev.type === 'read') {
      // Outro leu minhas mensagens — atualiza ✓✓
      setMessages(prev => prev.map(x => (x.to_user_id === ev.reader_id && !x.read_at) ? { ...x, read_at: ev.read_at } : x));
    } else if (ev.type === 'delivered') {
      setMessages(prev => prev.map(x => x.id === ev.message_id ? { ...x, delivered_at: ev.delivered_at } : x));
    } else if (ev.type === 'presence') {
      setUsers(prev => prev.map(u => u.id === ev.user_id ? { ...u, status: ev.status } : u));
    } else if (ev.type === 'typing') {
      const from = ev.from_user_id;
      if (ev.is_typing) {
        setTypingUsers(prev => {
          if (prev[from]) clearTimeout(prev[from]);
          const t = setTimeout(() => {
            setTypingUsers(p => { const c = { ...p }; delete c[from]; return c; });
          }, 4000);
          return { ...prev, [from]: t };
        });
      } else {
        setTypingUsers(prev => {
          if (prev[from]) clearTimeout(prev[from]);
          const c = { ...prev }; delete c[from]; return c;
        });
      }
    }
  }, [selected, user, open]);

  const { connected, sendJson, forceStatus } = useChatSocket({
    enabled: chatEnabled && !!token,
    token,
    onEvent: handleWsEvent,
  });

  // Carrega lista de usuários quando abre o painel ou fica online
  const loadUsers = useCallback(async () => {
    try {
      const r = await chatAPI.listUsers();
      setUsers(r.data || []);
    } catch (e) {
      // ignore
    }
  }, []);

  const loadUnread = useCallback(async () => {
    try {
      const r = await chatAPI.unreadCount();
      setTotalUnread(r.data?.total || 0);
    } catch (e) { void e; }
  }, []);

  useEffect(() => {
    if (chatEnabled) {
      loadUnread();
      const t = setInterval(loadUnread, 60000);
      return () => clearInterval(t);
    }
  }, [chatEnabled, loadUnread]);

  useEffect(() => {
    if (chatEnabled && open) loadUsers();
  }, [chatEnabled, open, loadUsers]);

  // Abrir conversa
  const openConversation = async (u) => {
    setSelected(u);
    setView('conversation');
    setMessages([]);
    try {
      const r = await chatAPI.history(u.id);
      setMessages(r.data || []);
      // marca como lidas
      if ((u.unread || 0) > 0) {
        await chatAPI.markRead(u.id);
        setUsers(prev => prev.map(x => x.id === u.id ? { ...x, unread: 0 } : x));
        setTotalUnread(t => Math.max(0, t - (u.unread || 0)));
      }
    } catch (e) {
      toast.error('Erro ao carregar histórico');
    }
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 50);
  };
  // Enviar mensagem
  const sendMessage = async () => {
    const body = text.trim();
    if (!body || !selected) return;
    setText('');
    // otimista
    const optimistic = {
      id: 'tmp-' + Date.now(),
      from_user_id: user.id,
      to_user_id: selected.id,
      body,
      sent_at: new Date().toISOString(),
      read_at: null,
      delivered_at: null,
      _optimistic: true,
    };
    setMessages(prev => [...prev, optimistic]);
    try {
      await chatAPI.send(selected.id, body);
      // a mensagem real chegará via WS; removemos o otimista quando o real chegar (mesmo body/from)
      setTimeout(() => {
        setMessages(prev => prev.filter(x => !x._optimistic || x.body !== body));
      }, 200);
    } catch (e) {
      toast.error('Falha ao enviar');
      setMessages(prev => prev.filter(x => x.id !== optimistic.id));
    }
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  // Emite typing (throttle 2s)
  const emitTyping = () => {
    if (!selected) return;
    const now = Date.now();
    if (now - typingSentAt.current > 2000) {
      typingSentAt.current = now;
      sendJson({ type: 'typing', to_user_id: selected.id, is_typing: true });
    }
  };

  // Muda status manual
  const changeStatus = async (status) => {
    setShowStatusMenu(false);
    setManualStatus(status);
    forceStatus(status);
    try {
      await chatAPI.updatePresence(status);
    } catch (e) { void e; }
  };

  // Ao rolar até o final quando novas mensagens chegam
  useEffect(() => {
    if (view === 'conversation') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, view]);

  // Marca lida quando abre a conversa e recebe uma mensagem nova
  useEffect(() => {
    if (selected && open && view === 'conversation') {
      const unread = messages.some(m => m.to_user_id === user?.id && m.from_user_id === selected.id && !m.read_at);
      if (unread) {
        chatAPI.markRead(selected.id).catch(() => {});
      }
    }
  }, [messages, selected, open, view, user]);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const s = search.toLowerCase();
    return users.filter(u => (u.nome || '').toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s));
  }, [users, search]);

  const myStatusEffective = manualStatus || (connected ? 'online' : 'offline');

  if (!chatEnabled || !user) return null;

  return (
    <>
      {/* Bolha flutuante */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-bubble"
          className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105"
          aria-label="Abrir chat"
        >
          <MessageCircle className="w-6 h-6" />
          {totalUnread > 0 && (
            <span
              data-testid="chat-badge"
              className="absolute -top-1 -right-1 bg-rose-500 text-white text-[11px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center border-2 border-white"
            >
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </button>
      )}

      {/* Painel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-5 right-5 z-40 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-2rem)] bg-white rounded-xl shadow-2xl flex flex-col border border-slate-200"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-emerald-600 text-white rounded-t-xl">
            {view === 'conversation' && selected ? (
              <>
                <button onClick={() => { setView('list'); setSelected(null); }} data-testid="chat-back" className="hover:bg-emerald-700 rounded p-1">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="flex-1 mx-2 min-w-0">
                  <p className="font-semibold text-sm truncate">{selected.nome}</p>
                  <p className="text-[11px] opacity-90 flex items-center gap-1">
                    <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[selected.status] || STATUS_COLORS.offline}`} />
                    {STATUS_LABEL[selected.status] || 'Offline'}
                  </p>
                </div>
                <button onClick={() => setOpen(false)} data-testid="chat-close" className="hover:bg-emerald-700 rounded p-1">
                  <X className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">Chat</p>
                  <button
                    className="text-[11px] opacity-90 flex items-center gap-1 hover:opacity-100"
                    onClick={() => setShowStatusMenu(v => !v)}
                    data-testid="chat-status-btn"
                  >
                    <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[myStatusEffective]}`} />
                    Seu status: {STATUS_LABEL[myStatusEffective]}
                    <span className="text-[9px]">▼</span>
                  </button>
                  {showStatusMenu && (
                    <div className="absolute left-4 top-16 bg-white text-slate-900 rounded-lg shadow-xl border py-1 w-40 z-10" data-testid="chat-status-menu">
                      {['online', 'ausente', 'ocupado'].map(s => (
                        <button
                          key={s}
                          onClick={() => changeStatus(s)}
                          data-testid={`status-${s}`}
                          className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 flex items-center gap-2"
                        >
                          <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[s]}`} />
                          {STATUS_LABEL[s]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={() => setOpen(false)} data-testid="chat-close" className="hover:bg-emerald-700 rounded p-1">
                  <X className="w-4 h-4" />
                </button>
              </>
            )}
          </div>

          {/* Body */}
          {view === 'list' ? (
            <>
              <div className="px-3 py-2 border-b">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar usuário..."
                    data-testid="chat-search"
                    className="w-full pl-8 pr-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {filteredUsers.length === 0 && (
                  <p className="text-center text-xs text-slate-500 py-8">Nenhum usuário encontrado</p>
                )}
                {filteredUsers.map(u => (
                  <button
                    key={u.id}
                    onClick={() => openConversation(u)}
                    data-testid={`chat-user-${u.id}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 border-b text-left transition-colors"
                  >
                    <div className="relative">
                      <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center justify-center text-sm">
                        {(u.nome || '?').charAt(0).toUpperCase()}
                      </div>
                      <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${STATUS_COLORS[u.status] || STATUS_COLORS.offline}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm truncate">{u.nome}</p>
                        {u.last_message && (
                          <span className="text-[10px] text-slate-400 shrink-0 ml-2">{fmtTime(u.last_message.sent_at)}</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-slate-500 truncate">
                          {u.last_message ? (u.last_message.from_user_id === user.id ? 'Você: ' : '') + (u.last_message.body || '') : STATUS_LABEL[u.status]}
                        </p>
                        {u.unread > 0 && (
                          <span className="bg-emerald-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1.5 flex items-center justify-center ml-2 shrink-0">
                            {u.unread}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50" data-testid="chat-messages">
                {messages.length === 0 && (
                  <p className="text-center text-xs text-slate-400 py-8">Nenhuma mensagem ainda. Diga oi! 👋</p>
                )}
                {messages.map(m => {
                  const mine = m.from_user_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] px-3 py-1.5 rounded-lg text-sm ${mine ? 'bg-emerald-600 text-white' : 'bg-white border border-slate-200 text-slate-900'}`}>
                        <p className="whitespace-pre-wrap break-words">{m.body}</p>
                        <p className={`text-[10px] mt-0.5 flex items-center gap-1 justify-end ${mine ? 'text-emerald-100' : 'text-slate-400'}`}>
                          {fmtTime(m.sent_at)}
                          {mine && (
                            m.read_at ? <span title="Lida">✓✓</span>
                            : m.delivered_at ? <span title="Entregue">✓✓</span>
                            : <span title="Enviada">✓</span>
                          )}
                        </p>
                      </div>
                    </div>
                  );
                })}
                {selected && typingUsers[selected.id] && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs italic text-slate-500">
                      digitando...
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
              <div className="border-t p-2 flex items-center gap-2">
                <input
                  value={text}
                  onChange={(e) => { setText(e.target.value); emitTyping(); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder="Digite uma mensagem..."
                  data-testid="chat-input"
                  className="flex-1 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
                <button
                  onClick={sendMessage}
                  disabled={!text.trim()}
                  data-testid="chat-send"
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md p-2 transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          )}

          {/* Indicador de status da conexão */}
          {!connected && (
            <div className="text-[10px] text-center py-1 bg-amber-50 text-amber-700 border-t" data-testid="chat-offline">
              Reconectando...
            </div>
          )}
        </div>
      )}
    </>
  );
}
